import type {
  InitializationSchema,
  PresetParameters,
} from "../types/pdm.types";

export function schemaFieldKeys(schema: InitializationSchema | undefined) {
  return Object.keys(schema?.properties ?? {});
}

type SchemaJsonTransformResult = {
  params: Record<string, unknown>;
  errors: string[];
};

function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseComplexParamsFromSchema(args: {
  schema: InitializationSchema | undefined;
  rawParams: Record<string, unknown>;
  omitEmpty: boolean;
  forceParse?: Record<string, "object" | "array">;
}): SchemaJsonTransformResult {
  const { schema, rawParams, omitEmpty, forceParse } = args;
  const props = schema?.properties ?? {};
  const params: Record<string, unknown> = { ...rawParams };
  const errors: string[] = [];

  Object.entries(rawParams).forEach(([key, raw]) => {
    const def = props[key];
    const schemaType = def?.type;
    const type = forceParse?.[key] ?? schemaType;
    const isComplex = type === "object" || type === "array";
    if (!isComplex) {
      return;
    }

    if (typeof raw !== "string") {
      return;
    }

    if (raw.trim() === "") {
      if (omitEmpty) {
        delete params[key];
      }
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      errors.push(`The field ${key} must be a valid JSON format`);
      return;
    }

    if (type === "array" && !Array.isArray(parsed)) {
      errors.push(`The field ${key} must be a JSON array`);
      return;
    }
    if (type === "object" && !isJsonObject(parsed)) {
      errors.push(`The field ${key} must be a JSON object`);
      return;
    }

    params[key] = parsed;
  });

  return { params, errors };
}

/**
 * Build `requiredParameters` and `optionalParameters` for POST /deployment.
 * Schema `network` is excluded because deployment target network is sent
 * top-level on the payload. Keys with a non-empty preset address are omitted
 * (stored server-side).
 */
export function buildDeploymentParametersPayload(
  values: Record<string, string>,
  mandatoryKeys: string[],
  optionalKeys: string[],
  presetParameters: PresetParameters | undefined,
): {
  requiredParameters: Record<string, unknown>;
  optionalParameters: Record<string, unknown>;
} {
  const presetKeys = new Set<string>();
  if (presetParameters) {
    Object.entries(presetParameters).forEach(([key, p]) => {
      if (p?.address?.trim()) {
        presetKeys.add(key);
      }
    });
  }

  const requiredParameters: Record<string, unknown> = {};
  const optionalParameters: Record<string, unknown> = {};

  mandatoryKeys.forEach((key) => {
    if (key === "network" || presetKeys.has(key)) {
      return;
    }
    const v = values[key];
    if (v !== undefined && v !== "") {
      requiredParameters[key] = v;
    }
  });

  optionalKeys.forEach((key) => {
    if (key === "network" || presetKeys.has(key)) {
      return;
    }
    const v = values[key];
    if (v !== undefined && v !== "") {
      optionalParameters[key] = v;
    }
  });

  return { requiredParameters, optionalParameters };
}

export function validateSchemaFields(
  schema: InitializationSchema | undefined,
  values: Record<string, string>,
): string[] {
  const errors: string[] = [];
  const required = schema?.required ?? [];
  const props = schema?.properties ?? {};

  required.forEach((key) => {
    if (key === "network") {
      return;
    }
    const raw = values[key];
    if (raw === undefined || String(raw).trim() === "") {
      errors.push(`${key} is required`);
      return;
    }
    const def = props[key];
    if (def?.pattern) {
      try {
        const re = new RegExp(def.pattern);
        if (!re.test(String(raw))) {
          errors.push(`${key} does not match the required pattern`);
        }
      } catch {
        /* ignore invalid pattern from schema */
      }
    }
  });
  return errors;
}
